import clsx from "clsx";
import { FC } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { priceFormatter } from "rujira.js";
import { Decimal, i18n, useWindowSize } from "rujira.ui";
import { HistoryFragment$key } from "./__generated__/HistoryFragment.graphql";
import { HistoryItemFragment$key } from "./__generated__/HistoryItemFragment.graphql";
import { TradeBreakpoint } from "../Trade";

const fragment = graphql`
  fragment HistoryFragment on FinTradeEdge {
    cursor
    node {
      id
      ...HistoryItemFragment
    }
  }
`;

export const HistoryFallback: FC = () => {
  return (
    <>
      <i18n.h3 className="fs-12 fw-500 mb-2 color-white">
        Market History
      </i18n.h3>
      <div
        className={clsx({
          "table-sticky": true,
          "pr-1": true,
        })}>
        <table className="table table--sm">
          <thead>
            <tr>
              <th style={{ width: "33%" }}>Price (...)</th>
              <th className="text-right">Amount (...)</th>
              <th className="text-right" style={{ width: "33%" }}>
                Time
              </th>
            </tr>
          </thead>
          <tbody className="mono fs-12">
            {[...Array(12)].map((_, i) => (
              <tr key={`history-skeleton-${i}`}>
                <td key={`skeleton-h-${i}`}>
                  <span className="inline-block skeleton w-full h-1 br-2" />
                </td>
                <td>
                  <span className="inline-block skeleton w-full h-1 br-2" />
                </td>
                <td>
                  <span className="inline-block skeleton w-full h-1 br-2" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const History: FC<{
  base: string;
  quote: string;
  tick: number;
  k: HistoryFragment$key[];
}> = ({ k, base, quote, tick }) => {
  const { width } = useWindowSize();

  return (
    <>
      <i18n.h3 className="fs-12 fw-500 mb-2 color-white">
        Market History
      </i18n.h3>
      <div
        className={clsx({
          "table-sticky": true,
          "pr-1": width > TradeBreakpoint,
        })}>
        <table className="table table--sm">
          <thead>
            <tr>
              <th style={{ width: "33%" }}>Price ({quote})</th>
              <th className="text-right">Amount ({base})</th>
              <th className="text-right" style={{ width: "33%" }}>
                Time
              </th>
            </tr>
          </thead>
          <tbody className="mono fs-12">
            {k
              ?.slice(0, width > TradeBreakpoint ? 80 : 10)
              .map((e, idx) => <HistoryItem key={idx} k={e} tick={tick} />)}
          </tbody>
        </table>
      </div>
    </>
  );
};

export const historyItemfragment = graphql`
  fragment HistoryItemFragment on FinTrade {
    type
    quoteAmount
    baseAmount
    price
    timestamp
  }
`;

export const HistoryItem: FC<{ tick: number; k: HistoryFragment$key }> = ({
  tick,
  k,
}) => {
  const i = useFragment(fragment, k);
  const d = useFragment<HistoryItemFragment$key>(historyItemfragment, i.node);
  if (!d) return null;
  return (
    <tr>
      <td className={d.type === "sell" ? "color-red" : "color-teal"}>
        {priceFormatter(BigInt(d.price), {
          precision: tick,
        })}
      </td>
      <td className="text-right">
        <Decimal
          subscript
          amount={BigInt(d.baseAmount)}
          round={4}
          className="color-grey"
        />
      </td>
      <td className="text-right color-grey mono fs-12">
        {new Date(d.timestamp).toLocaleTimeString("en-GB")}
      </td>
    </tr>
  );
};
